import AppsIcon from '@material-design-icons/svg/filled/apps.svg?react';
import TuneIcon from '@material-design-icons/svg/filled/tune.svg?react';
import { type DragEvent, useState } from 'react';

import { SearchableSelect } from '../../../components/SearchableSelect';
import type { DraftFormController } from '../useDraftForm';
import { MAX_SPOTLIGHT_APPS } from '../useDraftForm';
import type {
  CandidateRun,
  LoadDappReviews,
  SpotlightAppChoice,
  SpotlightAppOption,
} from '../types';
import { packageKey } from '../utils';
import { AppEntryRow } from './AppEntryRow';
import { CandidateSuggestions } from './CandidateSuggestions';
import { Section } from './Section';

interface CuratePanelProps {
  draft: DraftFormController;
  appOptions: SpotlightAppOption[];
  isLoadingApps: boolean;
  readOnly: boolean;
  weekLocked: boolean;
  candidateRuns: CandidateRun[];
  isGenerating: boolean;
  loadReviews: LoadDappReviews;
  onGenerateCandidates: () => void;
}

function fieldClass(extra?: string) {
  return `h-11 w-full rounded-xl border border-neutral-800 bg-neutral-900/50 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500/60 focus:outline-none disabled:opacity-60 ${extra ?? ''}`;
}

export function CuratePanel({
  draft,
  appOptions,
  isLoadingApps,
  readOnly,
  weekLocked,
  candidateRuns,
  isGenerating,
  loadReviews,
  onGenerateCandidates,
}: CuratePanelProps) {
  const { form } = draft;
  const [appPickerKey, setAppPickerKey] = useState(0);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const selectedPackages = new Set(
    form.appEntries.map((entry) => packageKey(entry.androidPackage)),
  );

  const appByPackage = new Map<string, SpotlightAppOption>();
  for (const app of appOptions) {
    if (app.packageName) {
      appByPackage.set(packageKey(app.packageName), app);
    }
  }

  const appChoices: SpotlightAppChoice[] = appOptions
    .filter((app) => app.packageName)
    .map((app) => ({
      key: app.packageName ?? '',
      label: `${app.dappName} (${app.packageName})`,
      packageName: app.packageName ?? '',
    }))
    .filter((choice) => !selectedPackages.has(packageKey(choice.key)));

  const pickerPlaceholder = isLoadingApps
    ? 'Loading apps…'
    : !draft.canAddApp
      ? `Maximum ${MAX_SPOTLIGHT_APPS} apps reached`
      : appChoices.length === 0
        ? 'All apps added'
        : 'Search and add an app';

  const handleAddChoice = (choice?: SpotlightAppChoice) => {
    if (choice) {
      draft.addApp(choice.packageName);
      setAppPickerKey((current) => current + 1);
    }
  };

  const resetDrag = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-4">
      <Section
        title="Details"
        icon={<TuneIcon className="size-4 fill-current" />}
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-neutral-400">
                Week ID
              </span>
              <input
                aria-label="Week ID"
                value={form.weekId}
                disabled={readOnly || weekLocked}
                onChange={(event) =>
                  draft.setField('weekId', event.target.value)
                }
                placeholder="2026-W22"
                className={fieldClass()}
              />
              {weekLocked && !readOnly && (
                <span className="text-[11px] text-neutral-600">
                  Week can't change after publishing.
                </span>
              )}
            </label>
            <label className="space-y-1.5 md:col-span-2">
              <span className="text-xs font-medium text-neutral-400">
                Theme title
              </span>
              <input
                aria-label="Theme Title"
                value={form.themeTitle}
                disabled={readOnly}
                onChange={(event) =>
                  draft.setField('themeTitle', event.target.value)
                }
                placeholder="Mobile games to play this week"
                className={fieldClass()}
              />
            </label>
          </div>

          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-neutral-400">
              Header blurb
            </span>
            <textarea
              aria-label="Header Blurb"
              value={form.headerBlurb}
              disabled={readOnly}
              onChange={(event) =>
                draft.setField('headerBlurb', event.target.value)
              }
              rows={2}
              maxLength={500}
              placeholder="A short intro shown above the spotlighted apps"
              className="w-full rounded-xl border border-neutral-800 bg-neutral-900/50 px-4 py-3 text-sm text-white placeholder:text-neutral-600 focus:border-teal-500/60 focus:outline-none disabled:opacity-60"
            />
          </label>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-neutral-400">
                Publish start
              </span>
              <input
                aria-label="Publish Start"
                type="datetime-local"
                value={form.publishStartAt}
                disabled={readOnly}
                onChange={(event) =>
                  draft.setField('publishStartAt', event.target.value)
                }
                className={fieldClass()}
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs font-medium text-neutral-400">
                Publish end
              </span>
              <input
                aria-label="Publish End"
                type="datetime-local"
                value={form.publishEndAt}
                disabled={readOnly}
                onChange={(event) =>
                  draft.setField('publishEndAt', event.target.value)
                }
                className={fieldClass()}
              />
            </label>
          </div>
        </div>
      </Section>

      <Section
        title={`Apps (${form.appEntries.length}/${MAX_SPOTLIGHT_APPS})`}
        icon={<AppsIcon className="size-4 fill-current" />}
      >
        <div className="space-y-3">
          {!readOnly && (
            <SearchableSelect
              key={appPickerKey}
              choices={appChoices}
              className="h-11 bg-neutral-900/50"
              placeholder={pickerPlaceholder}
              searchPlaceholder="Search apps…"
              disabled={
                isLoadingApps || !draft.canAddApp || appChoices.length === 0
              }
              onChange={handleAddChoice}
            />
          )}

          {form.appEntries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-800 bg-neutral-900/30 px-4 py-8 text-center text-sm text-neutral-500">
              No apps yet. Add apps with the picker above or from Suggestions.
            </div>
          ) : (
            <ul className="space-y-2">
              {form.appEntries.map((entry, index) => {
                const meta = appByPackage.get(packageKey(entry.androidPackage));
                const dappName = meta?.dappName ?? entry.androidPackage;
                return (
                  <AppEntryRow
                    key={entry.androidPackage}
                    entry={entry}
                    position={index + 1}
                    dappName={dappName}
                    dappId={meta?.id ?? null}
                    iconUrl={meta?.dappIconUrl ?? null}
                    readOnly={readOnly}
                    loadReviews={loadReviews}
                    isDragging={draggingIndex === index}
                    isDragOver={
                      dragOverIndex === index && draggingIndex !== index
                    }
                    onDragStart={(event: DragEvent<HTMLLIElement>) => {
                      setDraggingIndex(index);
                      event.dataTransfer.effectAllowed = 'move';
                      try {
                        event.dataTransfer.setData('text/plain', String(index));
                      } catch {
                        // Some browsers reject setData for non-text drags.
                      }
                    }}
                    onDragOver={(event: DragEvent<HTMLLIElement>) => {
                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      if (dragOverIndex !== index) {
                        setDragOverIndex(index);
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverIndex === index) {
                        setDragOverIndex(null);
                      }
                    }}
                    onDrop={(event: DragEvent<HTMLLIElement>) => {
                      event.preventDefault();
                      if (draggingIndex !== null && draggingIndex !== index) {
                        draft.moveApp(draggingIndex, index);
                      }
                      resetDrag();
                    }}
                    onDragEnd={resetDrag}
                    onCurationNoteChange={(value) =>
                      draft.setCurationNote(index, value)
                    }
                    onFeaturedReviewChange={(review) =>
                      draft.setFeaturedReview(index, review)
                    }
                    onRemove={() => draft.removeApp(index)}
                  />
                );
              })}
            </ul>
          )}
        </div>
      </Section>

      <CandidateSuggestions
        weekId={form.weekId}
        candidateRuns={candidateRuns}
        selectedPackages={selectedPackages}
        canAddApp={draft.canAddApp}
        isGenerating={isGenerating}
        readOnly={readOnly}
        onGenerate={onGenerateCandidates}
        onAddCandidate={draft.addCandidate}
      />
    </div>
  );
}
